/**
 * The event preview's contracts: which event card it shows, what the rule editor shares with it,
 * and what its history of past activations is made of. Not wire shapes — the queries are built
 * in the rule_estimate panel, and the rows they return are in [events](events.ts).
 */

import type { Rule } from './agents';
import type { EmbeddingHit, EventSearchResult } from './events';
import type { LoadState } from './snapshot';

/** The event card the preview shows: an event of a rule, by its position in the rule. */
export interface PreviewTarget {
    ruleId: string;
    eventIndex: number;
}

/** What the live part of an event's preview shows of the event's camera. */
export enum PreviewLive {
    /** The live video and the live score of the event's scored condition. */
    Level = 'level',
    /** The live video alone. */
    View = 'view',
}

/** Where the past moments of an event's preview come from. */
export enum PreviewHistory {
    /** A search of the archive's scene embeddings. */
    Scenes = 'scenes',
    /** A search of the detected objects. */
    Objects = 'objects',
}

/** What the preview of an event shows; null leaves that part out. */
export interface PreviewSections {
    live: PreviewLive | null;
    history: PreviewHistory | null;
}

/** What the rule editor shares with the preview while it edits a rule. */
export interface LiveRuleDraft {
    ruleId: string;
    draft: Rule;
    /**
     * Each draft event: true when the engine, which scores the saved rule, scores it as the draft
     * does. Otherwise its live levels show only after an apply.
     */
    scoredAsSaved: boolean[];
    /** Each draft event's position in the saved rule; null until the event is applied. */
    savedEventIndexes: (number | null)[];
    /** Goes up when an apply changes how the rule scores: the scores charted before are stale. */
    scoringEpoch: number;
}

/** The windows the estimate offers. */
export enum EstimatePresetKey {
    Hour = '1h',
    Shift = '8h',
    Day = '24h',
    Week = '7d',
}

/** One window the estimate offers, and how far back it reaches. */
export interface EstimatePreset {
    key: EstimatePresetKey;
    label: string;
    ms: number;
}

/** The fetched rows of a history that pass the event's threshold, newest first. */
export interface HistorySelection<Row> {
    rows: Row[];
    /** A page came back full of matches, so the real total can be higher. */
    isCapped: boolean;
}

/** The searches of one history: where the moments come from, and one query for each search. */
export interface EstimateHistoryRequest {
    source: PreviewHistory;
    queries: string[];
}

/** The pages of a history, one list for each search, in the order of the queries. */
export type FetchedHistory =
    | { source: PreviewHistory.Scenes; pages: EmbeddingHit[][] } |
    { source: PreviewHistory.Objects; pages: EventSearchResult[][] };

/** The pages of one history; null until they load. */
export interface EstimateHistorySnapshot extends LoadState {
    history: FetchedHistory | null;
}

/** A candidate activation as the preview lists it. */
export interface EstimateMoment {
    id: string;
    src?: string;
    timeMs: number;
    /** How well the moment matched the event's phrase. */
    similarity?: number;
    /** The zone of the camera's server, which the time reads in. */
    zone: string;
    cameraName?: string;
}
